import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Users, AlertTriangle, GitMerge, ExternalLink, CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';

const MERGEABLE_FIELDS = [
  'firstName', 'lastName', 'email', 'phone', 'companyPhone', 'companyEmail',
  'company', 'position', 'website', 'status', 'priority',
  'estimatedValue', 'currency', 'source'
];

const Duplicates = () => {
  const queryClient = useQueryClient();
  const [by, setBy] = useState('email');
  const [mergeState, setMergeState] = useState(null); // { group, keepId }

  const { data, isLoading } = useQuery(
    ['duplicates', by],
    () => api.get('/duplicates', { params: { by } }).then((r) => r.data)
  );

  const mergeMutation = useMutation(
    (body) => api.post('/duplicates/merge', body).then((r) => r.data),
    {
      onSuccess: (res) => {
        toast.success(`Merged ${res.mergedIds.length} lead(s) — ${res.noteCount} notes preserved`);
        setMergeState(null);
        queryClient.invalidateQueries(['duplicates']);
        queryClient.invalidateQueries(['leads']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Merge failed')
    }
  );

  const groups = data?.groups || [];

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Users size={28} /> Duplicate Leads
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Find and merge leads that match on the same normalized key. Merged leads are soft-deleted and restorable.
        </p>
      </div>

      {/* Detection rule */}
      <div className="flex items-center gap-3 mb-6">
        <span className="text-sm font-medium text-gray-700">Match by:</span>
        {[
          { key: 'email', label: 'Email (lowercased)' },
          { key: 'phone', label: 'Phone (last 10 digits)' }
        ].map((opt) => (
          <button
            key={opt.key}
            onClick={() => setBy(opt.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${by === opt.key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {opt.label}
          </button>
        ))}
        <span className="ml-auto text-sm text-gray-500">
          {isLoading ? 'Scanning...' : `${groups.length} duplicate group${groups.length === 1 ? '' : 's'} found`}
        </span>
      </div>

      {isLoading ? (
        <p className="text-gray-400">Scanning leads...</p>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
          <CheckCircle2 size={40} className="mx-auto text-green-500 mb-3" />
          <h3 className="font-semibold text-gray-700">No duplicates on {by}</h3>
          <p className="text-sm text-gray-500 mt-1">Every active lead has a unique {by === 'email' ? 'email address' : 'phone number'}.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <GroupCard
              key={g.key}
              group={g}
              onMerge={(keepId) => setMergeState({ group: g, keepId })}
            />
          ))}
        </div>
      )}

      {mergeState && (
        <MergeModal
          group={mergeState.group}
          keepId={mergeState.keepId}
          onCancel={() => setMergeState(null)}
          onConfirm={(body) => mergeMutation.mutate(body)}
          isLoading={mergeMutation.isLoading}
        />
      )}
    </div>
  );
};

const GroupCard = ({ group, onMerge }) => {
  const [primary, setPrimary] = useState(group.leads[0]._id);

  const fullName = (l) => `${l.firstName || ''} ${l.lastName || ''}`.trim() || '(no name)';

  return (
    <div className="bg-white border border-amber-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
        <AlertTriangle size={16} className="text-amber-600" />
        <span className="text-sm font-medium text-amber-900">
          {group.leads.length} leads share <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-amber-200">{group.key}</code>
        </span>
      </div>
      <div className="divide-y divide-gray-100">
        {group.leads.map((lead) => {
          const isPrimary = primary === lead._id;
          return (
            <div key={lead._id} className={`px-5 py-4 flex items-start gap-4 ${isPrimary ? 'bg-primary-50' : ''}`}>
              <input
                type="radio"
                checked={isPrimary}
                onChange={() => setPrimary(lead._id)}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/leads/${lead._id}`} className="font-semibold text-gray-900 hover:text-primary-600" target="_blank">
                    {fullName(lead)} <ExternalLink size={12} className="inline" />
                  </Link>
                  {lead.company && <span className="text-sm text-gray-600">@ {lead.company}</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{lead.status}</span>
                  {isPrimary && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 font-semibold">primary</span>}
                </div>
                <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-3">
                  {lead.email && <span>📧 {lead.email}</span>}
                  {lead.phone && <span>📞 {lead.phone}</span>}
                  {lead.estimatedValue > 0 && <span>💰 {formatCurrency(lead.estimatedValue, lead.currency || 'USD')}</span>}
                  {lead.noteCount > 0 && <span>📝 {lead.noteCount} note{lead.noteCount !== 1 ? 's' : ''}</span>}
                  <span>Created {new Date(lead.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end">
        <button
          onClick={() => onMerge(primary)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium"
        >
          <GitMerge size={14} /> Merge into primary
        </button>
      </div>
    </div>
  );
};

const MergeModal = ({ group, keepId, onCancel, onConfirm, isLoading }) => {
  const keep = group.leads.find((l) => l._id === keepId);
  const others = group.leads.filter((l) => l._id !== keepId);

  // Preview: for each fillable field, show resulting value
  const preview = useMemo(() => {
    const result = [];
    const isEmpty = (v) => v === null || v === undefined || v === '';
    for (const field of MERGEABLE_FIELDS) {
      const keepVal = keep?.[field];
      if (!isEmpty(keepVal)) {
        const conflicts = others.filter((o) => !isEmpty(o[field]) && String(o[field]) !== String(keepVal));
        if (conflicts.length > 0) {
          result.push({ field, resolution: 'keep-wins', value: keepVal, dropped: conflicts.map((c) => c[field]) });
        }
      } else {
        const source = others.find((o) => !isEmpty(o[field]));
        if (source) result.push({ field, resolution: 'fill-in', value: source[field], fromName: `${source.firstName} ${source.lastName}`.trim() });
      }
    }
    return result;
  }, [keep, others]);

  const totalNotes = group.leads.reduce((n, l) => n + (l.noteCount || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={isLoading ? undefined : onCancel} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <GitMerge size={20} /> Confirm merge
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Primary: <strong>{`${keep.firstName || ''} ${keep.lastName || ''}`.trim()}</strong> will absorb{' '}
            {others.length} other record{others.length === 1 ? '' : 's'} and keep all notes from every record.
          </p>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div className="text-sm">
            <strong>{totalNotes}</strong> note{totalNotes === 1 ? '' : 's'} will be combined onto the primary.
          </div>

          {preview.length === 0 ? (
            <div className="text-sm text-gray-500 italic">All fillable fields match or are only present on the primary. Clean merge.</div>
          ) : (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Field resolution</h4>
              <div className="bg-gray-50 rounded-lg divide-y divide-gray-100">
                {preview.map((p) => (
                  <div key={p.field} className="px-3 py-2 text-sm flex items-start gap-3">
                    <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-gray-200 flex-shrink-0">{p.field}</code>
                    <div className="flex-1">
                      {p.resolution === 'fill-in' ? (
                        <span className="text-green-700">
                          <strong>Filled in</strong> from {p.fromName}: <code>{String(p.value)}</code>
                        </span>
                      ) : (
                        <span className="text-amber-700">
                          <strong>Primary kept</strong>: <code>{String(p.value)}</code>
                          <span className="text-gray-500"> · dropped: {p.dropped.map(String).join(', ')}</span>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
            <strong>{others.length}</strong> record{others.length === 1 ? ' will be' : 's will be'} archived (soft-deleted, restorable).
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={isLoading} className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900">Cancel</button>
          <button
            onClick={() => onConfirm({ keepId: keep._id, mergeIds: others.map((o) => o._id) })}
            disabled={isLoading}
            className="inline-flex items-center gap-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
          >
            <GitMerge size={14} /> {isLoading ? 'Merging...' : 'Merge now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Duplicates;
